import { router } from "@inertiajs/react"
import { useEffect, useRef } from "react"

/**
 * OKS-37 — Poll Inertia partial-reload props every `intervalMs` while the
 * tab is visible; pause on `visibilitychange → hidden`; immediate reload
 * on `visibilitychange → visible` (catches up after the tab regains focus).
 *
 * No `localStorage`/`sessionStorage` (Crew rule). No timers run while
 * hidden (no background tab churn).
 *
 * @example
 *   usePollOnFocus({
 *     only: ["conversations", "messages", "unread_messages_count"],
 *     intervalMs: 30_000,
 *   })
 *
 * Mounted at the top of a page component; deactivates on unmount.
 */
interface UsePollOnFocusOpts {
  /** Inertia partial-data prop names to refresh. Empty array = full reload. */
  only: string[]
  /** Polling cadence in ms (default 30s — TRD §1). */
  intervalMs?: number
  /** When false, the hook is a no-op (e.g. paused during a modal). */
  enabled?: boolean
  /**
   * OKS-819 — under the server-rendered test-support flag ONLY, expose this
   * poll's EXACT reload on `window[name]` so a Playwright E2E can fire the
   * 30s poll on demand (no timer, no `visibilitychange`) with perfect
   * fidelity to the real `only` keys. Absent in dev/prod (the gating
   * `<meta name="test-support">` is test-only), so no seam ships. Opt-in per
   * caller (the messaging surfaces pass `"__oksMessagesReload"`).
   */
  exposeTestReloadHandle?: string
}

/**
 * OKS-491 precedent — true only when the app layout rendered
 * `<meta name="test-support" content="1">`, which
 * `ApplicationHelper#test_support_enabled?` emits ONLY on the Playwright Rails
 * server (`Rails.env.test?` AND `RAILS_TEST_SUPPORT_ENABLED=1`). Absent in
 * dev/prod and under the default RSpec run.
 */
function testSupportEnabled(): boolean {
  if (typeof document === "undefined") return false
  return (
    document
      .querySelector('meta[name="test-support"]')
      ?.getAttribute("content") === "1"
  )
}

export function usePollOnFocus({
  only,
  intervalMs = 30_000,
  enabled = true,
  exposeTestReloadHandle,
}: UsePollOnFocusOpts): void {
  // Ref keeps `only` stable across re-renders without forcing the useEffect
  // re-bind on every parent render (the array identity may change even when
  // contents are equal). The mirror-write happens in a separate effect so
  // we don't violate `react-hooks/refs` (no ref writes during render).
  const onlyRef = useRef(only)
  useEffect(() => {
    onlyRef.current = only
  }, [only])

  useEffect(() => {
    if (!enabled) return

    let timer: number | null = null

    function reload() {
      // `router.reload` preserves scroll + state by default; we just
      // need to scope the partial-data list. Drop extra options to
      // stay within the typed `ReloadOptions` surface.
      router.reload({ only: onlyRef.current })
    }

    // OKS-819 — test-support-only seam: expose THIS poll's exact reload so a
    // Playwright E2E fires the real 30s poll deterministically. Gated on the
    // server-rendered flag; never present in dev/prod.
    const exposeSeam = exposeTestReloadHandle && testSupportEnabled()
    if (exposeSeam) {
      ;(window as unknown as Record<string, () => void>)[
        exposeTestReloadHandle
      ] = reload
    }

    function start() {
      stop()
      timer = window.setInterval(reload, intervalMs)
    }

    function stop() {
      if (timer !== null) {
        window.clearInterval(timer)
        timer = null
      }
    }

    function onVisibilityChange() {
      if (document.hidden) {
        stop()
      } else {
        // Catch-up reload + restart cadence.
        reload()
        start()
      }
    }

    if (!document.hidden) start()
    document.addEventListener("visibilitychange", onVisibilityChange)

    return () => {
      stop()
      document.removeEventListener("visibilitychange", onVisibilityChange)
      if (exposeSeam) {
        delete (window as unknown as Record<string, unknown>)[
          exposeTestReloadHandle
        ]
      }
    }
  }, [enabled, intervalMs, exposeTestReloadHandle])
}
